/**
 * How a stop looks and what it is called, decided once: timeline blocks, the unscheduled rail,
 * map pins and legends, directory badges, and the monitoring panels.
 *
 * Two vocabularies live here. Slot types (`shipper_collection`, `depot_injection`, ...) are the
 * database enum and carry the colour scheme. Monitoring stops can also carry a coarse `type`
 * (`collection`, `delivery`, `depot`, `break`, `fuel`) from Samsara, which the `getStopType*`
 * helpers at the bottom also understand.
 */
import { SlotType } from "@/types/collections"
import { getRouteKindStopClasses } from "@/lib/routeKindDisplay"

export const SLOT_TYPE_LABELS: Record<SlotType, string> = {
  shipper_collection: "Shipper Collection",
  internal_stop: "Internal Stop",
  haulage_collection: "Haulage Collection",
  haulage_delivery: "Haulage Delivery",
  depot_injection: "Depot Injection",
  direct_injection: "Direct Injection",
  charging_stop: "Charging Stop",
  stand_by: "Stand-by",
}

export function getSlotTypeLabel(slotType?: SlotType | string): string {
  if (!slotType) return "Unknown"
  return SLOT_TYPE_LABELS[slotType as SlotType] ?? "Unknown"
}

/**
 * Map-pin fill and stroke for a slot type.
 *
 * `overlapping` is the case where several stops sit on top of each other at one site: the pin
 * stops standing for a type and starts standing for a pile, so it drops to neutral rather than
 * claiming to be whichever type happened to be drawn last.
 */
export function getSlotTypePinClasses(slotType?: SlotType | string, overlapping?: boolean): string {
  if (overlapping)
    return "text-palette-jungleGreen600 fill-palette-jungleGreen400 stroke-palette-jungleGreen600 drop-shadow-md"
  switch (slotType) {
    case "shipper_collection":
      return "text-blue-500 fill-blue-400 stroke-blue-600 drop-shadow-md"
    case "haulage_collection":
      return "text-emerald-500 fill-emerald-400 stroke-emerald-600 drop-shadow-md"
    case "haulage_delivery":
      return "text-green-500 fill-green-400 stroke-green-600 drop-shadow-md"
    case "depot_injection":
      return "text-amber-500 fill-amber-400 stroke-amber-600 drop-shadow-md"
    case "direct_injection":
      return "text-rose-500 fill-rose-400 stroke-rose-600 drop-shadow-md"
    case "internal_stop":
      return "text-purple-500 fill-purple-400 stroke-purple-600 drop-shadow-md"
    case "charging_stop":
      return "text-cyan-500 fill-cyan-400 stroke-cyan-600 drop-shadow-md"
    case "stand_by":
      return "text-slate-500 fill-slate-300 stroke-slate-500 drop-shadow-md"
    default:
      return "text-palette-jungleGreen500 fill-palette-jungleGreen400 stroke-palette-jungleGreen600 drop-shadow-md"
  }
}

/** The matching swatch for a legend entry, where a pin outline would be too fussy at 8px. */
export function getSlotTypeDotClasses(slotType?: SlotType | string): string {
  switch (slotType) {
    case "shipper_collection":
      return "bg-blue-400 border-blue-600"
    case "haulage_collection":
      return "bg-emerald-400 border-emerald-600"
    case "haulage_delivery":
      return "bg-green-400 border-green-600"
    case "depot_injection":
      return "bg-amber-400 border-amber-600"
    case "direct_injection":
      return "bg-rose-400 border-rose-600"
    case "internal_stop":
      return "bg-purple-400 border-purple-600"
    case "charging_stop":
      return "bg-cyan-400 border-cyan-600"
    case "stand_by":
      return "bg-slate-300 border-slate-500"
    default:
      return "bg-palette-jungleGreen400 border-palette-jungleGreen600"
  }
}

/** Left border and fill for a stop block on the planning timeline and the unscheduled rail. */
export function getSlotTypeBlockClasses(slotType?: SlotType | string): string {
  switch (slotType) {
    case "shipper_collection":
      return "border-l-blue-500 bg-blue-50"
    case "haulage_collection":
      return "border-l-emerald-500 bg-emerald-50"
    case "haulage_delivery":
      return "border-l-green-500 bg-green-50"
    case "depot_injection":
      return "border-l-amber-500 bg-amber-50"
    case "direct_injection":
      return "border-l-rose-500 bg-rose-50"
    case "internal_stop":
      return "border-l-purple-500 bg-purple-50"
    case "charging_stop":
      return "border-l-cyan-500 bg-cyan-50"
    case "stand_by":
      // Only reachable for a stand_by stop parked on a route that is not itself stand-by. Uses the
      // route-level token so the two cannot drift into two different-looking hatches.
      return getRouteKindStopClasses("standby")
    default:
      return "border-l-palette-jungleGreen500 bg-palette-jungleGreen000"
  }
}

/** Badge colours for a slot type in the directory tables. */
export function getSlotTypeBadgeClasses(slotType?: SlotType | string): string {
  switch (slotType) {
    case "shipper_collection":
      return "bg-blue-100 text-blue-700"
    case "haulage_collection":
      return "bg-emerald-100 text-emerald-700"
    case "haulage_delivery":
      return "bg-green-100 text-green-700"
    case "depot_injection":
      return "bg-amber-100 text-amber-700"
    case "direct_injection":
      return "bg-rose-100 text-rose-700"
    case "charging_stop":
      return "bg-cyan-100 text-cyan-700"
    default:
      return "bg-palette-jungleGreen100 text-palette-jungleGreen700"
  }
}

export function getSlotTypeEmoji(slotType?: SlotType | string): string {
  switch (slotType) {
    case "shipper_collection":
      return "📦"
    case "haulage_collection":
      return "🚛"
    case "haulage_delivery":
      return "🚚"
    case "depot_injection":
      return "🏭"
    case "direct_injection":
      return "➡️"
    case "charging_stop":
      return "🔌"
    default:
      return "📍"
  }
}

// Monitoring stops: either vocabulary.

export function getStopTypeIcon(type: string): string {
  switch (type) {
    case "collection":
      return "📦"
    case "delivery":
      return "🚚"
    case "depot":
      return "🏭"
    case "break":
      return "☕"
    case "fuel":
      return "⛽"
    case "charging_stop":
      return "🔌"
    case "direct_injection":
      return "➡️"
    default:
      return "📍"
  }
}

export type StopTypeColorVariant = "border" | "solid" | "badge"

export function getStopTypeColor(type: string, variant: StopTypeColorVariant = "border"): string {
  const normalized = (type || "").toLowerCase()
  switch (normalized) {
    case "collection":
      return variant === "border"
        ? "border-l-blue-500 bg-blue-50"
        : variant === "solid"
          ? "bg-blue-500"
          : "bg-blue-100 text-blue-700"
    case "delivery":
      return variant === "border"
        ? "border-l-emerald-500 bg-emerald-50"
        : variant === "solid"
          ? "bg-emerald-500"
          : "bg-emerald-100 text-emerald-700"
    case "depot":
      return variant === "border"
        ? "border-l-amber-500 bg-amber-50"
        : variant === "solid"
          ? "bg-amber-500"
          : "bg-amber-100 text-amber-700"
    case "break":
      return variant === "border"
        ? "border-l-purple-500 bg-purple-50"
        : variant === "solid"
          ? "bg-purple-500"
          : "bg-purple-100 text-purple-700"
    case "fuel":
      return variant === "border"
        ? "border-l-orange-500 bg-orange-50"
        : variant === "solid"
          ? "bg-orange-500"
          : "bg-orange-100 text-orange-700"
    case "charging_stop":
      return variant === "border"
        ? "border-l-cyan-500 bg-cyan-50"
        : variant === "solid"
          ? "bg-cyan-500"
          : "bg-cyan-100 text-cyan-700"
    case "direct_injection":
      return variant === "border"
        ? "border-l-rose-500 bg-rose-50"
        : variant === "solid"
          ? "bg-rose-500"
          : "bg-rose-100 text-rose-700"
    default:
      return variant === "border"
        ? "border-l-gray-500 bg-gray-50"
        : variant === "solid"
          ? "bg-gray-500"
          : "bg-gray-100 text-gray-700"
  }
}

/**
 * The short label the monitoring panels use ("Charging", "Haulage"), where the slot-type labels
 * above would not fit.
 */
export function getStopTypeLabel(type: string): string {
  const normalized = (type || "").toLowerCase()
  if (normalized === "charging_stop") return "Charging"
  if (normalized === "shipper_collection") return "Collection"
  if (normalized === "depot_injection") return "Depot"
  if (normalized === "direct_injection") return "Direct"
  if (normalized === "internal_stop") return "Internal"
  if (normalized === "haulage_delivery") return "Haulage"
  if (!normalized) return "Stop"
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/_/g, " ")
}
